import { useQuery, QueryResult } from '@apollo/client';
// Api
import { GET_NOTIFICATIONS } from 'api/notifications/queries';
// Types
import {
  GetNotifications,
  GetNotificationsVariables,
  GetNotifications_getNotifications_data,
} from 'api/notifications/types/GetNotifications';
import { OnNotificationCreated_onNotificationCreated } from 'api/notifications/types/OnNotificationCreated';
// Hooks
import { useGetCurrUser } from 'hooks/useGetCurrUser';
import {
  commonOptions,
  useNotificationSubscription,
} from './useNotificationSubscription';
import { useHasMounted } from 'hooks/useHasMounted';

type UseGetNotificationsProps = {
  limit?: number;
  nextToken?: string | null;
  skipApi?: boolean;
};

/**
 * Hook to fetch notifications with real-time subscription support
 *
 * @param props - Optional props for pagination
 * @returns The query result with notifications data
 */
export const useGetNotifications = ({
  limit = 10,
  nextToken = null,
  skipApi = false,
}: UseGetNotificationsProps = {}): QueryResult<
  GetNotifications,
  GetNotificationsVariables
> => {
  const hasMounted = useHasMounted();
  const { data: userData } = useGetCurrUser();
  const userId = userData?.me.id;
  const skip = !userId || !hasMounted || skipApi;

  const result = useQuery<GetNotifications, GetNotificationsVariables>(
    GET_NOTIFICATIONS,
    commonOptions(skip, limit, nextToken)
  );

  useNotificationSubscription<
    GetNotifications,
    GetNotifications_getNotifications_data
  >(
    result.subscribeToMore,
    result.data,
    userId,
    'getNotifications',
    undefined,
    (notification: OnNotificationCreated_onNotificationCreated) =>
      (notification as unknown) as GetNotifications_getNotifications_data
  );

  return result;
};
